import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Sparkles, Heart, BookOpen, MessageCircle, Compass, Clock } from 'lucide-react';
import { MemorySection } from '@/components/landing/MemorySection';
import { PhaseCard } from '@/components/landing/PhaseCard';
import { DisclaimerFooter } from '@/components/DisclaimerFooter';

// Floating Glow Orbs Component
const GlowOrbs = () => (
  <>
    <div 
      className="glow-orb glow-orb-amber" 
      style={{ width: '450px', height: '450px', top: '5%', right: '5%' }} 
    />
    <div 
      className="glow-orb glow-orb-rose" 
      style={{ width: '350px', height: '350px', top: '40%', left: '-5%' }} 
    />
    <div 
      className="glow-orb glow-orb-sage" 
      style={{ width: '400px', height: '400px', bottom: '15%', right: '15%' }} 
    />
  </>
);

// Hero Section with glow effect
const HeroSection = ({ user, token }) => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user && token) {
      navigate('/sanctuary');
    } else {
      navigate('/signup');
    }
  };

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center pt-20 pb-16 px-4 overflow-hidden">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          background: 'linear-gradient(180deg, #F8F5F0 0%, #EDE8E0 100%)',
        }}
      />
      
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Soft glow orb behind text */}
        <div className="flex justify-center mb-8">
          <div 
            className="w-28 h-28 rounded-full opacity-70"
            style={{
              background: 'radial-gradient(circle, rgba(201,168,76,0.6) 0%, rgba(232,180,184,0.3) 50%, transparent 100%)',
              boxShadow: '0 0 80px rgba(201,168,76,0.4)',
              animation: 'pulse 4s ease-in-out infinite',
            }}
          />
        </div>

        {/* Tagline */}
        <p 
          className="text-sm md:text-base tracking-[0.2em] uppercase mb-6"
          style={{ color: '#a89968' }}
        >
          For Women Ready to Reclaim Themselves
        </p>

        {/* Main Headline */}
        <h1 
          className="text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight mb-6 leading-tight"
          style={{ fontFamily: 'Playfair Display, serif', color: '#2C2C2C' }}
        >
          Your journey back to <span className="italic" style={{ color: '#C9A84C' }}>yourself</span> begins here
        </h1>

        {/* Subhead */}
        <p 
          className="text-xl md:text-2xl mb-8 leading-relaxed"
          style={{ fontFamily: 'Playfair Display, serif', color: '#5a5248', fontStyle: 'italic' }}
          data-testid="hero-subhead"
        >
          You're not broken — you're rebuilding.
        </p>

        {/* Supporting copy */}
        <div className="max-w-2xl mx-auto mb-10 space-y-4">
          <p className="text-base md:text-lg leading-relaxed text-stone-600">
            Sage meets you where you are with daily guidance that actually lands, phase-based healing that makes sense, and a companion that remembers what you're going through.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-stone-600">
            Some days you need clarity. Some days you just need to be heard. This is a space for all of it — without overcomplication, without judgment.
          </p>
        </div>

        {/* CTA Button with gradient */}
        <Button 
          onClick={handleGetStarted}
          size="lg"
          className="btn-gradient rounded-full px-10 py-6 text-base md:text-lg text-[#FBF9F6] border-0"
          data-testid="get-started-btn"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          {user && token ? 'Enter Your Sanctuary' : 'Begin Your Healing Journey'}
        </Button>
        
        <p className="text-sm text-stone-400 mt-4">
          14-day free trial · Cancel anytime · Start today
        </p>
      </div>

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.05); opacity: 0.9; }
        }
      `}</style>
    </section>
  );
};

// Inner World Teaser Section
const InnerWorldTeaserSection = ({ user, token }) => {
  const navigate = useNavigate();

  const handleLearnMore = () => {
    navigate('/inner-world-preview');
  };

  return (
    <section 
      className="relative py-20 md:py-28 px-4 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #EDE8E0 0%, #E4DDD1 100%)' }}
      data-testid="inner-world-teaser"
    >
      <div className="relative z-10 max-w-3xl mx-auto text-center">
        <p 
          className="text-sm md:text-base tracking-[0.2em] uppercase mb-6"
          style={{ color: '#a89968' }}
        >
          Coming to Sage
        </p>
        <h2 
          className="text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight mb-8 leading-tight"
          style={{ fontFamily: 'Playfair Display, serif', color: '#2C2C2C' }}
        >
          A world that <span className="italic" style={{ color: '#C9A84C' }}>grows</span> as you do
        </h2>
        <div className="max-w-2xl mx-auto mb-10 space-y-4">
          <p className="text-base md:text-lg leading-relaxed text-stone-600">
            Some of what you're carrying doesn't come out in words right away. The Inner World gives it somewhere else to go — a quiet, unhurried space where the parts of you that protect, avoid, or hide finally get met instead of managed.
          </p>
          <p className="text-base md:text-lg leading-relaxed text-stone-600">
            No pressure to go deeper than you're ready for. No score to keep. Just a place that remembers you, and waits exactly where you left it.
          </p>
        </div>
        <Button
          onClick={handleLearnMore}
          variant="outline"
          size="lg"
          className="rounded-full px-8 py-6 text-base bg-transparent"
          style={{ borderColor: '#C9A84C', color: '#a89968' }}
          data-testid="inner-world-learn-more-btn"
        >
          Learn More
        </Button>
      </div>
    </section>
  );
};

// Meet Sage Section with dark theme and glass
const MeetSageSection = () => (
  <section className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#FBF9F6' }}>
    <GlowOrbs />
    
    <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
      <p 
        className="text-sm tracking-[0.2em] uppercase mb-6"
        style={{ color: 'rgba(201,168,76,0.8)' }}
      >
        Meet Sage
      </p>
      <h2
        className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight mb-8 leading-relaxed"
        style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
      >
        A trauma-informed AI companion for women rebuilding after relational loss.
      </h2>
      <p 
        className="text-lg md:text-xl leading-relaxed mb-4"
        style={{ color: '#4A4A5A' }}
      >
        Not a therapist. Not a hotline. Something in between.
      </p>
      <p 
        className="text-base md:text-lg italic"
        style={{ color: '#9C685D', fontFamily: 'Playfair Display, serif' }}
      >
        A presence that holds space when you need to say the thing out loud at 2am.
      </p>
    </div>
  </section>
);

// Bento Grid Features Section
const BentoFeaturesSection = () => (
  <section className="relative py-20 overflow-hidden" style={{ backgroundColor: '#FBF9F6' }}>
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Card 1: Spans 2 columns */}
        <div className="md:col-span-2 glass-strong rounded-2xl p-8 md:p-10 hover-lift">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
            style={{ backgroundColor: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.2)' }}
          >
            <Compass className="w-7 h-7" style={{ color: '#C9A84C' }} />
          </div>
          <h3 
            className="text-2xl md:text-3xl font-semibold mb-4"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
          >
            Phase-Based Guidance
          </h3>
          <p className="text-lg leading-relaxed" style={{ color: '#4A4A5A' }}>
            Sage meets you in your current phase and takes you forward in the right order — instead of forcing rushed healing.
          </p>
        </div>

        {/* Card 2: Single column */}
        <div className="glass-strong rounded-2xl p-8 md:p-10 hover-lift">
          <div 
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
            style={{ backgroundColor: 'rgba(232,180,184,0.1)', border: '1px solid rgba(232,180,184,0.2)' }}
          >
            <Clock className="w-7 h-7" style={{ color: '#A9756B' }} />
          </div>
          <h3 
            className="text-2xl font-semibold mb-4"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
          >
            Available Any Time
          </h3>
          <p className="leading-relaxed" style={{ color: '#4A4A5A' }}>
            No scheduling. No waiting. A calm place to process — including the moments no one else sees.
          </p>
        </div>

        {/* Card 3: Full width */}
        <div className="md:col-span-3 glass rounded-2xl p-8 md:p-10 hover-lift flex flex-col md:flex-row gap-8 items-start md:items-center">
          <div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(125,157,140,0.1)', border: '1px solid rgba(125,157,140,0.2)' }}
          >
            <BookOpen className="w-8 h-8" style={{ color: '#7d9d8c' }} />
          </div>
          <div>
            <h3 
              className="text-2xl md:text-3xl font-semibold mb-3"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
            >
              Continuous Memory
            </h3>
            <p className="text-lg leading-relaxed" style={{ color: '#4A4A5A' }}>
              Sage remembers your triggers, patterns, milestones, and strengths across every conversation. You never have to re-explain yourself.
            </p>
          </div>
        </div>

      </div>
    </div>
  </section>
);

// The Healing System Section
const HealingSystemSection = () => (
  <section className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#FBF9F6' }}>
    <div className="max-w-6xl mx-auto px-6 md:px-12">
      <div className="text-center mb-16">
        <p 
          className="text-sm tracking-[0.2em] uppercase mb-4"
          style={{ color: '#C9A84C' }}
        >
          The System
        </p>
        <h2
          className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight mb-6"
          style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
        >
          Four phases. Your pace.
        </h2>
        <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed" style={{ color: '#4A4A5A' }}>
          Each phase builds on the last. You always know where you are.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <PhaseCard
          number="1"
          title="Stabilize"
          description="Ground yourself. Learn to regulate your nervous system. Create safety in your body and mind before diving deeper."
          delay={0}
        />
        <PhaseCard
          number="2"
          title="Understand"
          description="Map what happened. Identify patterns. See how your past shaped your present — without judgment."
          delay={100}
        />
        <PhaseCard
          number="3"
          title="Reclaim"
          description="Take back what was lost. Rebuild trust in yourself. Rewrite the narratives that no longer serve you."
          delay={200}
        />
        <PhaseCard
          number="4"
          title="Renew"
          description="Step into who you're becoming. Build new patterns. Create a life aligned with who you actually are."
          delay={300}
        />
      </div>
    </div>
  </section>
);

// Features Section (Light theme)
const FeaturesSection = () => (
  <section className="py-16 md:py-20 bg-stone-50">
    <div className="max-w-5xl mx-auto px-6 md:px-12">
      <div className="text-center mb-12">
        <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: '#C9A84C' }}>
          Your Sanctuary Includes
        </p>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
          Everything you need to rebuild
        </h2>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {[
          { icon: MessageCircle, title: 'AI Companion — Sage', description: 'Trauma-informed support available 24/7. Not a chatbot — a presence trained to hold space.' },
          { icon: Heart, title: 'Phase-Based Healing', description: 'Structured journey through grief, rebuilding, and rediscovery with prompts that meet you where you are.' },
          { icon: BookOpen, title: 'Guided Journaling', description: 'Daily prompts personalized to your healing phase. Process, reflect, and track your growth.' },
          { icon: Sparkles, title: 'Daily Rituals', description: 'Morning check-ins, breathwork, and somatic exercises designed for emotional regulation.' },
        ].map((feature) => {
          const Icon = feature.icon;
          return (
            <div key={feature.title} className="p-6 rounded-2xl bg-white border border-stone-100 shadow-sm hover-lift">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(201,168,76,0.1)' }}>
                <Icon className="w-6 h-6" style={{ color: '#C9A84C' }} />
              </div>
              <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {feature.title}
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">{feature.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Pricing Section
const PricingSection = ({ user, token }) => {
  const navigate = useNavigate();

  const handleSelectPlan = (plan) => {
    if (user && token) {
      navigate('/checkout', { state: { selectedPlan: plan } });
    } else {
      navigate('/signup', { state: { selectedPlan: plan } });
    }
  };

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: '#C9A84C' }}>
            Pricing
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Start your 14-day free trial
          </h2>
          <p className="text-stone-600">No credit card required. Cancel anytime.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Monthly */}
          <div className="p-8 rounded-2xl border-2 bg-white hover-lift" style={{ borderColor: 'rgba(201,168,76,0.3)' }}>
            <p className="text-sm tracking-[0.15em] uppercase mb-2" style={{ color: '#C9A84C' }}>Monthly</p>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>$37</span>
              <span className="text-stone-500">/month</span>
            </div>
            <ul className="space-y-3 mb-8 text-sm text-stone-600">
              {['Unlimited Sage conversations', 'Phase-based healing journey', 'Daily personalized prompts', 'Guided journaling with export', 'Morning rituals & breathwork'].map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <span style={{ color: '#C9A84C' }}>✓</span> {f}
                </li>
              ))}
            </ul>
            <Button onClick={() => handleSelectPlan('monthly')} className="w-full rounded-full py-6" variant="outline" style={{ borderColor: '#C9A84C', color: '#C9A84C' }} data-testid="select-monthly-btn">
              Start Free Trial
            </Button>
          </div>

          {/* Annual */}
          <div className="p-8 rounded-2xl relative overflow-hidden hover-lift" style={{ background: 'linear-gradient(135deg, #FBF9F6 0%, #1a1a24 100%)', border: '2px solid #C9A84C' }}>
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs" style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}>Best Value</div>
            <p className="text-sm tracking-[0.15em] uppercase mb-2" style={{ color: '#C9A84C' }}>Annual</p>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="text-4xl font-semibold text-[#1A1A2E]" style={{ fontFamily: 'Playfair Display, serif' }}>$333</span>
              <span className="text-stone-400">/year</span>
            </div>
            <p className="text-sm text-stone-400 mb-6">$27.75/month · Save $111</p>
            <ul className="space-y-3 mb-8 text-sm text-stone-300">
              {['Everything in Monthly', '25% savings', 'Founding member pricing locked', 'Extended session memory', 'Early access to new features'].map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <span style={{ color: '#C9A84C' }}>✓</span> {f}
                </li>
              ))}
            </ul>
            <Button onClick={() => handleSelectPlan('annual')} className="w-full rounded-full py-6 border-0" style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }} data-testid="select-annual-btn">
              Start Free Trial
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA
const FinalCTASection = () => (
  <section className="relative py-20 md:py-28 overflow-hidden" style={{ backgroundColor: '#FBF9F6' }}>
    <GlowOrbs />
    <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12 text-center">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-normal tracking-tight mb-6 leading-relaxed" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
        You've been carrying this alone<br />long enough.
      </h2>
      <p className="text-lg mb-8 leading-relaxed" style={{ color: '#4A4A5A' }}>
        Your sanctuary is ready when you are.
      </p>
      <a href="/signup" className="btn-gradient inline-flex items-center justify-center rounded-full px-10 py-4 text-base font-medium text-[#FBF9F6]">
        Start Your Free Trial
      </a>
    </div>
  </section>
);

// Main Landing Page
export const LandingPage = () => {
  const { user, token } = useAuth();

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in', 'revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.memory-card, .phase-card, .reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      <HeroSection user={user} token={token} />
      <InnerWorldTeaserSection user={user} token={token} />
      <MeetSageSection />
      <BentoFeaturesSection />
      <MemorySection />
      <HealingSystemSection />
      <FeaturesSection />
      <PricingSection user={user} token={token} />
      <FinalCTASection />
      <DisclaimerFooter />
    </div>
  );
};

export default LandingPage;
